const TERMINAL_STATUS_WORDS = ['approved', 'rejected', 'declined', 'closed', 'completed', 'complete', 'cancelled', 'canceled', 'done'];

function make_validation_item(key, severity, title, message) {
  return {
    key: String(key),
    severity: String(severity),
    title: String(title),
    message: String(message),
  };
}

function is_plain_object(source) {
  return Boolean(source) && typeof source === 'object' && !Array.isArray(source);
}

function unknown_array(value) {
  const items = [];
  if (!Array.isArray(value)) return items;
  value.forEach((item) => {
    items.push(item);
  });
  return items;
}

function clone_plain_object(source) {
  const result = {};
  Reflect.ownKeys(source).forEach((entry_key) => {
    if (typeof entry_key !== 'string') return;
    const descriptor = Reflect.getOwnPropertyDescriptor(source, entry_key);
    if (!descriptor?.enumerable) return;
    Reflect.set(result, entry_key, Reflect.get(source, entry_key));
  });
  return result;
}

const SAFE_KEYS = new Set([
  'action', 'action_name', 'actionTrigger', 'actions', 'assignedFormStatusId',
  'assignedForms', 'assignedReviewers', 'assignedStatuses', 'assignmentId',
  'color', 'colorCode', 'color_code', 'conditionsSummary', 'error',
  'formSectionId', 'form_name', 'form_status_id', 'id', 'incoming', 'info',
  'isFinal', 'isInitial', 'is_final', 'is_initial', 'minApprovals', 'name',
  'normalizedIndex', 'outgoing', 'persistedFinal', 'persistedInitial',
  'sectionAccessMatrix', 'sectionAccessRows', 'sectionName', 'severity',
  'source', 'sourceId', 'state', 'statusId', 'statusKey', 'statusName',
  'status_id', 'status_name', 'target', 'targetId', 'terminal',
  'transitionType', 'transitions', 'type', 'warning', 'workflowActions',
]);

const STATUS_COLOR_CLASS_MAP = {
  '#0d6efd': 'preview-flow-dot-blue',
  '#0b63f6': 'preview-flow-dot-blue',
  '#3b82f6': 'preview-flow-dot-blue',
  blue: 'preview-flow-dot-blue',
  '#198754': 'preview-flow-dot-green',
  '#168a4a': 'preview-flow-dot-green',
  '#22c55e': 'preview-flow-dot-green',
  green: 'preview-flow-dot-green',
  '#dc3545': 'preview-flow-dot-red',
  '#ef4444': 'preview-flow-dot-red',
  red: 'preview-flow-dot-red',
  '#ffc107': 'preview-flow-dot-yellow',
  '#f59e0b': 'preview-flow-dot-yellow',
  yellow: 'preview-flow-dot-yellow',
  orange: 'preview-flow-dot-yellow',
  '#8b5cf6': 'preview-flow-dot-purple',
  '#7c3aed': 'preview-flow-dot-purple',
  purple: 'preview-flow-dot-purple',
};

function safe_get(source, key) {
  if (!is_plain_object(source)) return undefined;
  const safe_key = String(key || '');
  if (!SAFE_KEYS.has(safe_key)) return undefined;
  if (!Reflect.getOwnPropertyDescriptor(source, safe_key)) return undefined;
  const value = Reflect.get(source, safe_key);
  const safe_value = value;
  return safe_value;
}

export function safe_string(source, key, fallback = '') {
  const value = safe_get(source, key);
  return value == null ? fallback : String(value);
}

export function safe_number(source, key, fallback = 0) {
  const value = safe_get(source, key);
  const number_value = Number(value);
  return Number.isFinite(number_value) ? number_value : fallback;
}

export function safe_boolean(source, key, fallback = false) {
  const value = safe_get(source, key);
  return value == null ? fallback : Boolean(value);
}

export function safe_array(source, key) {
  const value = safe_get(source, key);
  const items = unknown_array(value);
  return items;
}

export function safe_object(source, key) {
  const value = safe_get(source, key);
  const object_value = is_plain_object(value) ? clone_plain_object(value) : {};
  return object_value;
}

function safe_array_by_entry(source, target_key) {
  if (!is_plain_object(source)) return [];
  const safe_key = String(target_key);
  const descriptor = Reflect.getOwnPropertyDescriptor(source, safe_key);
  if (!descriptor?.enumerable) return [];
  const value = Reflect.get(source, safe_key);
  const items = unknown_array(value);
  return items;
}

export function status_color_class(color) {
  const normalized = String(color || '').trim().toLowerCase();
  const color_entry = Object.entries(STATUS_COLOR_CLASS_MAP).find(([key]) => key === normalized);
  return Array.isArray(color_entry) ? color_entry[1] : 'preview-flow-dot-default';
}

export function first_value(source, keys, fallback = '') {
  if (!Array.isArray(keys)) return fallback;
  const found_key = keys.find((key) => safe_string(source, key, '').trim() !== '');
  return found_key ? safe_string(source, found_key, fallback) : fallback;
}

function number_value(source, keys) {
  if (!Array.isArray(keys)) return 0;
  const found_key = keys.find((key) => safe_string(source, key, '').trim() !== '');
  return found_key ? safe_number(source, found_key) : 0;
}

function string_value(source, keys) {
  return String(first_value(source, keys, '')).trim().toLowerCase();
}

export function status_looks_terminal(status = {}) {
  const name = string_value(status, ['name', 'status_name']);
  return TERMINAL_STATUS_WORDS.some((word) => name.includes(word));
}

function transition_id(transition, direction) {
  return safe_number(transition, direction === 'source' ? 'sourceId' : 'targetId');
}

function transition_name(transition, direction) {
  return string_value(transition, [direction === 'source' ? 'source' : 'target']);
}

function status_has_matching_id(transition_id_value, assigned_id, status_id) {
  return Boolean((assigned_id && transition_id_value === assigned_id) || (status_id && transition_id_value === status_id));
}

function status_has_matching_name(transition_name_value, status_name) {
  return Boolean(transition_name_value && status_name && transition_name_value === status_name);
}

export function transition_matches_status(transition, status, direction) {
  const transition_id_value = transition_id(transition, direction);
  const assigned_id = number_value(status, ['assignedFormStatusId', 'assignmentId', 'form_status_id']);
  const status_id = number_value(status, ['statusId', 'status_id', 'id']);
  const transition_name_value = transition_name(transition, direction);
  const status_name = string_value(status, ['name', 'status_name']);
  return status_has_matching_id(transition_id_value, assigned_id, status_id)
    || status_has_matching_name(transition_name_value, status_name);
}

export function is_automatic_transition(transition) {
  const type = string_value(transition, ['transitionType', 'type']);
  return type === 'automatic' || type.includes('automatic');
}

export function normalize_status(status, index, transitions) {
  const transition_items = Array.isArray(transitions) ? transitions : [];
  const assignedId = number_value(status, ['assignedFormStatusId', 'assignmentId', 'form_status_id']);
  const statusId = number_value(status, ['statusId', 'status_id', 'id']);
  const outgoing = transition_items.filter((transition) => transition_matches_status(transition, status, 'source'));
  const incoming = transition_items.filter((transition) => transition_matches_status(transition, status, 'target'));
  const explicitFinal = ['isFinal', 'is_final', 'persistedFinal'].some((key) => safe_boolean(status, key));

  return {
    normalizedIndex: index,
    assignedId,
    statusId,
    statusKey: assignedId || statusId || safe_string(status, 'name') || index,
    statusName: first_value(status, ['name', 'status_name'], 'Unknown status'),
    color: first_value(status, ['colorCode', 'color_code']),
    outgoing,
    incoming,
    terminal: explicitFinal || status_looks_terminal(status),
    isInitial: safe_boolean(status, 'isInitial'),
    is_initial: safe_boolean(status, 'is_initial'),
    persistedInitial: safe_boolean(status, 'persistedInitial'),
  };
}

function form_label(form) {
  return String(first_value(form, ['name', 'form_name'], '')).trim();
}

export function assigned_form_label(forms) {
  const names = (Array.isArray(forms) ? forms : [])
    .map((form) => form_label(form))
    .filter(Boolean);
  if (names.length === 0) return 'No form assigned';
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

function action_name_from(actions, action_id) {
  const action = (Array.isArray(actions) ? actions : []).find((item) => safe_string(item, 'id') === String(action_id));
  return first_value(action, ['action_name', 'name']);
}

export function section_access_summary(rows, matrix, actions, reviewers) {
  const sections = (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const form_section_id = safe_string(row, 'formSectionId');
      const action_ids = safe_array_by_entry(matrix, form_section_id);
      return {
        sectionName: safe_string(row, 'sectionName'),
        actions: action_ids.map((action_id) => action_name_from(actions, action_id)).filter(Boolean),
      };
    })
    .filter((row) => safe_array(row, 'actions').length);

  if (!sections.length) return [];

  const reviewerName = (Array.isArray(reviewers) ? reviewers : [])
    .map((reviewer) => safe_string(reviewer, 'label'))
    .filter(Boolean)
    .join(', ') || 'Selected reviewer';
  const uniqueActions = [...new Set(
    sections.reduce((items, row) => items.concat(safe_array(row, 'actions')), [])
  )];
  return [{ reviewerName, actions: uniqueActions, sections }];
}

export function condition_label(transition) {
  return safe_string(transition, 'conditionsSummary').trim() || 'No conditions';
}

export function validation_items_for_status(status, transitions) {
  const items = [];
  const status_key = safe_string(status, 'statusKey');
  const status_name = safe_string(status, 'statusName');
  const outgoing = safe_array(status, 'outgoing');
  const incoming = safe_array(status, 'incoming');

  if (!outgoing.length && safe_boolean(status, 'terminal')) {
    items.push(make_validation_item(`terminal-${status_key}`, 'INFO', 'Terminal status', `${status_name} has no outgoing transitions. No further transitions are expected.`));
  } else if (!outgoing.length && transitions.length) {
    items.push(make_validation_item(`no-out-${status_key}`, 'WARNING', 'Status has no outgoing transition', `${status_name} has no outgoing transition and is not marked as a final status.`));
  }
  if (transitions.length && !incoming.length && safe_number(status, 'normalizedIndex') > 0) {
    items.push(make_validation_item(`unreachable-${status_key}`, 'WARNING', 'Workflow contains unreachable status', `${status_name} is not targeted by any transition.`));
  }

  return items;
}

export function status_validation_items(normalized_statuses, transitions) {
  const statuses = Array.isArray(normalized_statuses) ? Array.from(normalized_statuses) : [];
  const transition_items = Array.isArray(transitions) ? Array.from(transitions) : [];
  return statuses.flatMap((status) => validation_items_for_status(status, transition_items));
}

export function validation_items_for_transition(transition, assigned_reviewers) {
  const items = [];
  const reviewers = Array.isArray(assigned_reviewers) ? Array.from(assigned_reviewers) : [];
  const automatic = is_automatic_transition(transition);
  const transition_id_value = safe_string(transition, 'id');
  const transition_source = safe_string(transition, 'source', 'Source');
  const transition_target = safe_string(transition, 'target');
  const action_trigger = first_value(transition, ['actionTrigger', 'action']);

  if (!transition_target || safe_number(transition, 'targetId') <= 0) {
    items.push(make_validation_item(`missing-target-${transition_id_value}`, 'ERROR', 'Transition has no target status', `${transition_source || 'A transition'} is missing its destination status.`));
  }
  if (!automatic && !action_trigger.trim()) {
    items.push(make_validation_item(`missing-trigger-${transition_id_value}`, 'ERROR', 'Manual transition has no trigger action', `${transition_source || 'Source'} to ${transition_target || 'target'} needs a button/action trigger.`));
  }
  if (!automatic && !reviewers.length) {
    items.push(make_validation_item(`missing-reviewers-${transition_id_value}`, 'WARNING', 'Status has no reviewers assigned', `${transition_source || 'Source'} has manual movement and needs reviewers assigned.`));
  }
  if (automatic && condition_label(transition) === 'No conditions') {
    items.push(make_validation_item(`missing-conditions-${transition_id_value}`, 'WARNING', 'Automatic transition has no conditions', `${transition_source || 'Source'} to ${transition_target || 'target'} should define when the system moves it.`));
  }

  return items;
}

export function transition_validation_items(transitions, assigned_reviewers) {
  const transition_items = Array.isArray(transitions) ? Array.from(transitions) : [];
  const reviewers = Array.isArray(assigned_reviewers) ? Array.from(assigned_reviewers) : [];
  return transition_items.flatMap((transition) => validation_items_for_transition(transition, reviewers));
}

function base_validation_items(forms, statuses, transition_items) {
  const items = [];
  if (!forms.length) {
    items.push(make_validation_item('no-forms', 'ERROR', 'No form assigned', 'Assign at least one form before activating this workflow.'));
  }
  if (!statuses.length) {
    items.push(make_validation_item('no-statuses', 'ERROR', 'No statuses assigned', 'Add statuses so records have a valid workflow path.'));
  }
  if (!transition_items.length) {
    items.push(make_validation_item('no-transitions', 'ERROR', 'No transitions configured', 'Connect statuses with manual or automatic transition rules.'));
  }
  return items;
}

function section_access_validation_items(reviewers, access_summary) {
  if (!reviewers.length || access_summary.length) return [];
  return [
    make_validation_item('no-section-access', 'WARNING', 'No section access configured', 'Reviewers are assigned, but no explicit section permissions are available in this preview context.'),
  ];
}

export function preview_validation_items(assigned_forms, assigned_statuses, transitions, assigned_reviewers, section_access_summary_items, normalized_statuses) {
  const forms = Array.isArray(assigned_forms) ? Array.from(assigned_forms) : [];
  const statuses = Array.isArray(assigned_statuses) ? Array.from(assigned_statuses) : [];
  const transition_items = Array.isArray(transitions) ? Array.from(transitions) : [];
  const reviewers = Array.isArray(assigned_reviewers) ? Array.from(assigned_reviewers) : [];
  const access_summary = Array.isArray(section_access_summary_items) ? Array.from(section_access_summary_items) : [];

  return [
    ...base_validation_items(forms, statuses, transition_items),
    ...status_validation_items(normalized_statuses, transition_items),
    ...transition_validation_items(transition_items, reviewers),
    ...section_access_validation_items(reviewers, access_summary),
  ];
}

export function validation_summary(items) {
  const summary = { error: 0, warning: 0, info: 0 };
  const validation_items = Array.isArray(items) ? Array.from(items) : [];

  validation_items.forEach((item) => {
    const key = safe_string(item, 'severity').toLowerCase();
    if (key === 'error') summary.error += 1;
    if (key === 'warning') summary.warning += 1;
    if (key === 'info') summary.info += 1;
  });

  return summary;
}

export function workflow_health(summary) {
  const error_count = safe_number(summary, 'error');
  const warning_count = safe_number(summary, 'warning');
  const info_count = safe_number(summary, 'info');

  if (error_count) {
    return { state: 'Incomplete', detail: `${error_count} error${error_count === 1 ? '' : 's'} must be fixed before activation.` };
  }
  if (warning_count) {
    return { state: 'Warning', detail: `${warning_count} warning${warning_count === 1 ? '' : 's'} should be reviewed before activation.` };
  }
  if (info_count) {
    return { state: 'Ready', detail: `${info_count} informational note${info_count === 1 ? '' : 's'} found. No blockers detected.` };
  }
  return { state: 'Ready', detail: 'Ready for production review' };
}

export function health_badge_class(health) {
  const state = safe_string(health, 'state');
  if (state === 'Ready') return 'bg-success-subtle text-success';
  if (state === 'Invalid') return 'bg-danger-subtle text-danger';
  if (state === 'Warning') return 'bg-warning-subtle text-dark';
  return 'bg-secondary-subtle text-secondary';
}

export function health_icon(health) {
  const state = safe_string(health, 'state');
  if (state === 'Ready') return 'bi-check-circle';
  if (state === 'Invalid') return 'bi-x-circle';
  if (state === 'Warning') return 'bi-exclamation-triangle';
  return 'bi-info-circle';
}

export function trigger_label(transition) {
  return is_automatic_transition(transition)
    ? 'Auto'
    : first_value(transition, ['actionTrigger', 'action'], 'Missing trigger');
}

export function readable_condition_label(transition) {
  const label = condition_label(transition);
  if (label === 'No conditions') return label;
  return label
    .replace(/complete questions?,?\s*/i, 'all required questions are completed; ')
    .replace(/(\d+) required question(s?)/i, '$1 required question$2 completed')
    .replace(/Questions:\s*/i, 'question ')
    .replace(/Sections:\s*/i, 'sections ')
    .replace(/Required actions:\s*/i, 'required actions completed: ')
    .replace(/,\s*question\s+/i, '; question: ')
    .replace(/\s*;\s*,?\s*/g, '; ')
    .replace(/\s{2,}/g, ' ')
    .replace(/;\s*$/g, '')
    .trim()
    .replace(/\s*\u00e2\u20ac\u00a2\s*/g, '. ');
}

export function transition_action_label(transition) {
  return is_automatic_transition(transition) ? 'Automatic move' : trigger_label(transition);
}

export function transition_type_explanation(transition) {
  return is_automatic_transition(transition)
    ? 'AUTOMATIC: Moves automatically when conditions are met.'
    : `MANUAL: User clicks "${trigger_label(transition)}".`;
}

export function human_transition_sentence(transition) {
  const source = safe_string(transition, 'source', 'source');
  const target = safe_string(transition, 'target', 'target');
  if (is_automatic_transition(transition)) {
    const condition = readable_condition_label(transition);
    return condition === 'No conditions'
      ? `Moves automatically from ${source} to ${target} when conditions are met.`
      : `Moves automatically from ${source} to ${target} when ${condition}.`;
  }
  return `When user clicks "${first_value(transition, ['actionTrigger', 'action'], 'Trigger')}", move ${source} to ${target}.`;
}



